import React from "react";

// FDA Vertical Nutrition Facts label — 21 CFR 101.9 compliant layout.
// Uses Helvetica, strict black & white, thick rules.
// For liquid products, serving size is shown as both grams and mL per
// 21 CFR 101.9(b)(7) (e.g., "30g (30 mL)").
export default function NutritionLabel({ calc, isLiquid = false, density = 1.0, servingCountDisplay, servingSizeDisplay }) {
  if (!calc) return null;
  const { nutrition, serving_size_g, servings_per_container } = calc;
  const n = nutrition.per_serving;
  const dv = nutrition.daily_values;
  const servingMl = isLiquid ? Math.round(serving_size_g / (density || 1)) : null;
  const fmt = (v, unit = "g") => {
    if (v === 0) return `0${unit}`;
    if (v === 0.5) return `<1${unit}`;
    if (unit === "g") return `${v}${unit}`;
    return `${v}${unit}`;
  };
  return (
    <div className="print-label font-fda bg-white text-black w-full max-w-[320px] border-[3px] border-black p-2 select-none"
      data-testid="nutrition-label">
      <div className="text-[28px] font-black leading-none tracking-tight">Nutrition Facts</div>
      <div className="text-[11px] mt-0.5">{servingCountDisplay || calc.servings_per_container_auto?.display || `${servings_per_container} ${servings_per_container === 1 ? "serving" : "servings"} per container`}</div>
      <div className="flex justify-between items-end border-b-[8px] border-black pb-0.5">
        <div className="text-[13px] font-bold">Serving size</div>
        <div className="text-[13px] font-bold">{servingSizeDisplay || (calc.serving_measure ? `${calc.serving_measure} (${serving_size_g} g)` : isLiquid ? `${serving_size_g}g (${servingMl} mL)` : `${serving_size_g}g`)}</div>
      </div>

      <div className="text-[10px] mt-1">Amount per serving</div>
      <div className="flex justify-between items-end border-b-4 border-black pb-0.5">
        <div className="text-[22px] font-black leading-none">Calories</div>
        <div className="text-[24px] font-black leading-none">{n.calories}</div>
      </div>

      <div className="text-[10px] text-right font-bold mt-0.5">% Daily Value*</div>

      <Row bold label="Total Fat" val={fmt(n.total_fat_g)} dv={dv.total_fat_g} />
      <Row indent label="Saturated Fat" val={fmt(n.saturated_fat_g)} dv={dv.saturated_fat_g} />
      <Row indent label={<><i>Trans</i> Fat</>} val={fmt(n.trans_fat_g)} />
      <Row bold label="Cholesterol" val={fmt(n.cholesterol_mg, "mg")} dv={dv.cholesterol_mg} />
      <Row bold label="Sodium" val={fmt(n.sodium_mg, "mg")} dv={dv.sodium_mg} />
      <Row bold label="Total Carbohydrate" val={fmt(n.total_carbs_g)} dv={dv.total_carbs_g} />
      <Row indent label="Dietary Fiber" val={fmt(n.fiber_g)} dv={dv.fiber_g} />
      <Row indent label="Total Sugars" val={fmt(n.sugars_g)} />
      <Row indent2 label={<>Includes {fmt(n.added_sugars_g)} Added Sugars</>} dv={dv.added_sugars_g} />
      <div className="border-b-[8px] border-black" />
      <Row bold label="Protein" val={fmt(n.protein_g)} />
      <div className="border-b-[3px] border-black mt-0.5" />

      <MicroRow label="Vitamin D" val={`${n.vitamin_d_mcg}mcg`} dv={dv.vitamin_d_mcg} />
      <MicroRow label="Calcium" val={`${n.calcium_mg}mg`} dv={dv.calcium_mg} />
      <MicroRow label="Iron" val={fmt(n.iron_mg, "mg")} dv={dv.iron_mg} />
      <MicroRow label="Potassium" val={`${n.potassium_mg}mg`} dv={dv.potassium_mg} last />

      <div className="text-[9px] mt-2 leading-tight">
        * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet.
        2,000 calories a day is used for general nutrition advice.
      </div>
    </div>
  );
}

function Row({ label, val, dv, bold, indent, indent2 }) {
  return (
    <div className={`flex justify-between border-b border-black py-0.5 text-[13px] ${indent ? "pl-4" : ""} ${indent2 ? "pl-8" : ""}`}>
      <div>
        <span className={bold ? "font-bold" : ""}>{label}</span>
        {val !== undefined && <span className={bold ? "" : ""}> {val}</span>}
      </div>
      {dv !== undefined && <div className="font-bold">{dv}%</div>}
    </div>
  );
}

function MicroRow({ label, val, dv, last }) {
  return (
    <div className={`flex justify-between ${last ? "" : "border-b border-black"} py-0.5 text-[12px]`}>
      <div>{label} {val}</div>
      <div>{dv}%</div>
    </div>
  );
}
